import ZhCN from '@ll-plus/locale/lang/zh-cn'
import { get } from 'lodash-es'
import { computed, inject, isRef, ref, unref } from 'vue'

import type { ILocalLanguage } from '@ll-plus/locale'
import type { MaybeRef } from '@vueuse/core'
import type { InjectionKey, Ref } from 'vue'

export type TranslatorOption = Record<string, string | number>
export type Translator = (path: string, option?: TranslatorOption) => string
export type LocaleContext = {
  locale: Ref<ILocalLanguage>
  lang: Ref<string>
  t: Translator
}

export const buildTranslator =
  (locale: MaybeRef<ILocalLanguage>): Translator =>
  (path, option) =>
    translate(path, option, unref(locale))

export const translate = (
  path: string,
  option: undefined | TranslatorOption,
  locale: ILocalLanguage
): string =>
  (get(locale, path, path) as string).replace(
    /\{(\w+)\}/g,
    (_, key) => `${option?.[key] ?? `{${key}}`}`
  )

export const buildLocaleContext = (
  locale: MaybeRef<ILocalLanguage>
): LocaleContext => {
  const lang = computed(() => unref(locale).name)
  const localeRef = isRef(locale) ? locale : ref(locale)
  return {
    lang,
    locale: localeRef,
    t: buildTranslator(locale),
  }
}

export const localeContextKey: InjectionKey<Ref<ILocalLanguage | undefined>> =
  Symbol('localeContextKey')

export const useLocale = (
  localeOverrides?: Ref<ILocalLanguage | undefined>
) => {
  const locale = localeOverrides || inject(localeContextKey, ref())!
  return buildLocaleContext(computed(() => locale.value || ZhCN) as any)
}
